import { useMemo, useState } from 'react';
import {
  CheckCircle2,
  Copy,
  Download,
  FileCheck2,
  FilterX,
  RefreshCw,
  Settings2,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useColumnState } from '@/hooks/useColumnState';
import { copyCSVToClipboard, downloadCSV, toCSV, type CSVColumn } from '@/lib/csv';
import { cn } from '@/lib/utils';
import { DataTable, type DataTableColumn } from '@/shared/components/data-table';
import { Pagination } from '@/shared/components/Pagination';
import type {
  ValidationRequirement,
  ValidationRequirementFilters,
  ValidationRequirementStatus,
} from '../../types/validation.types';

type ValidationColumn = DataTableColumn<ValidationRequirement> & {
  defaultVisible?: boolean;
};

const CSV_COLUMNS: CSVColumn<ValidationRequirement>[] = [
  { header: 'Requirement', value: (row) => row.requirementCode },
  { header: 'Module', value: (row) => moduleLabel(row.module) },
  { header: 'Source', value: (row) => row.sourceReference },
  { header: 'Requirement statement', value: (row) => row.statement },
  { header: 'Acceptance criteria', value: (row) => row.acceptanceCriteria },
  { header: 'Implementation reference', value: (row) => row.implementationReference },
  { header: 'Validation status', value: (row) => row.status },
  { header: 'Evidence records', value: (row) => row.evidenceCount },
  { header: 'Latest evidence result', value: (row) => row.latestEvidence?.result || '' },
  { header: 'Latest signer', value: (row) => row.latestSignature?.signerName || '' },
  { header: 'Latest signed at UTC', value: (row) => row.latestSignature?.signedAt || '' },
];

function moduleLabel(module: string) {
  return {
    document_control: 'Document Control',
    change_control: 'Change Control',
    capa: 'CAPA',
    compliance: 'Cross-Module Compliance',
    training: 'Training Management',
  }[module] || module.split('_').join(' ');
}

const STATUS_PRESENTATION: Record<ValidationRequirementStatus, {
  label: string;
  className: string;
  icon: typeof FileCheck2;
}> = {
  mapped: {
    label: 'Mapped',
    className: 'border-sky-200 bg-sky-50 text-sky-800',
    icon: FileCheck2,
  },
  verified: {
    label: 'Verified',
    className: 'border-teal-200 bg-teal-50 text-teal-800',
    icon: CheckCircle2,
  },
  gap: {
    label: 'Gap',
    className: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: ShieldAlert,
  },
};

function formatUtc(value: string | null | undefined) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value)) + ' UTC';
}

function ValidationStatusBadge({ status }: { status: ValidationRequirementStatus }) {
  const presentation = STATUS_PRESENTATION[status];
  const Icon = presentation.icon;
  return (
    <Badge variant="outline" className={presentation.className}>
      <Icon className="mr-1 h-3.5 w-3.5" />
      {presentation.label}
    </Badge>
  );
}

interface ValidationRequirementsTableProps {
  requirements: ValidationRequirement[];
  total: number;
  page: number;
  pageLength: number;
  sortKey: ValidationRequirementFilters['sortKey'];
  sortDirection: 1 | -1;
  columnFilters: Record<string, string>;
  status: ValidationRequirementFilters['status'];
  module: string;
  isLoading: boolean;
  isRefreshing: boolean;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearFilters: () => void;
  onSort: (key: ValidationRequirementFilters['sortKey']) => void;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  onRecordEvidence: (requirement: ValidationRequirement) => void;
  onExportAll: () => Promise<ValidationRequirement[]>;
}

export function ValidationRequirementsTable({
  requirements,
  total,
  page,
  pageLength,
  sortKey,
  sortDirection,
  columnFilters,
  status,
  module,
  isLoading,
  isRefreshing,
  onColumnFilterChange,
  onClearFilters,
  onSort,
  onPageChange,
  onRefresh,
  onRecordEvidence,
  onExportAll,
}: ValidationRequirementsTableProps) {
  const [isExportingAll, setIsExportingAll] = useState(false);
  const columns = useMemo<ValidationColumn[]>(() => [
    {
      key: 'requirementCode',
      label: 'Requirement',
      sortKey: 'requirementCode',
      filterType: 'text',
      isPrimarySearch: true,
      searchPlaceholder: 'Search requirement or control…',
      getValue: (row) => row.requirementCode,
      render: (row) => (
        <div>
          <div className="font-mono text-xs font-semibold text-slate-950">
            {row.requirementCode}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">{row.sourceReference}</div>
        </div>
      ),
    },
    {
      key: 'module',
      label: 'Module',
      sortKey: 'module',
      noFilter: true,
      getValue: (row) => row.module,
      render: (row) => <span className="font-medium text-slate-800">{moduleLabel(row.module)}</span>,
    },
    {
      key: 'statement',
      label: 'Control and acceptance',
      filterType: 'text',
      getValue: (row) => `${row.statement} ${row.acceptanceCriteria}`,
      render: (row) => (
        <div className="max-w-xl py-1">
          <div className="font-medium leading-5 text-slate-900">{row.statement}</div>
          <div className="mt-1 line-clamp-2 text-[11px] leading-4 text-slate-500">
            Accept when: {row.acceptanceCriteria}
          </div>
        </div>
      ),
    },
    {
      key: 'implementationReference',
      label: 'Implemented control',
      filterType: 'text',
      defaultVisible: false,
      getValue: (row) => row.implementationReference,
      render: (row) => (
        <div className="max-w-sm whitespace-pre-wrap font-mono text-[11px] text-slate-600">
          {row.implementationReference}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Readiness',
      sortKey: 'status',
      noFilter: true,
      getValue: (row) => row.status,
      render: (row) => <ValidationStatusBadge status={row.status} />,
    },
    {
      key: 'evidenceCount',
      label: 'Evidence',
      noFilter: true,
      getValue: (row) => String(row.evidenceCount),
      render: (row) => (
        <div>
          <div className="font-semibold tabular-nums text-slate-900">{row.evidenceCount}</div>
          <div className="text-[11px] text-slate-500">
            {row.latestEvidence ? `Latest: ${row.latestEvidence.result}` : 'No signed result'}
          </div>
        </div>
      ),
    },
    {
      key: 'signature',
      label: 'Latest manifestation',
      noFilter: true,
      getValue: (row) => row.latestSignature?.signerName || '',
      render: (row) => row.latestSignature ? (
        <div className="max-w-xs">
          <div className="font-medium text-slate-800">{row.latestSignature.signerName}</div>
          <div className="text-[11px] text-slate-500">
            {row.latestSignature.meaning} · {formatUtc(row.latestSignature.signedAt)}
          </div>
          <div className="mt-1 line-clamp-2 text-[11px] italic text-slate-500">
            “{row.latestSignature.reason}”
          </div>
        </div>
      ) : <span className="text-slate-400">Unsigned</span>,
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: (row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            onRecordEvidence(row);
          }}
          aria-label={`Record evidence for ${row.requirementCode}`}
        >
          <FileCheck2 className="mr-1 h-4 w-4" /> Record evidence
        </Button>
      ),
    },
  ], [onRecordEvidence]);

  const columnState = useColumnState({
    columns,
    storageKeys: {
      visibility: 'atek-eqms-validation-columns',
      order: 'atek-eqms-validation-column-order',
      widths: 'atek-eqms-validation-column-widths',
    },
  });
  const hasFilters = Object.values(columnFilters).some(Boolean)
    || (status && status !== 'all')
    || Boolean(module);

  const exportRows = (rows: ValidationRequirement[], suffix: string) => {
    const filename = `eqms-traceability-${suffix}-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCSV(filename, toCSV(rows, CSV_COLUMNS));
    toast.success(`Exported ${rows.length} traceability records`);
  };

  const exportAll = async () => {
    try {
      setIsExportingAll(true);
      exportRows(await onExportAll(), 'all');
    } catch {
      toast.error('Could not export the traceability register');
    } finally {
      setIsExportingAll(false);
    }
  };

  const copyPage = async () => {
    try {
      await copyCSVToClipboard(toCSV(requirements, CSV_COLUMNS));
      toast.success(`Copied ${requirements.length} traceability records`);
    } catch {
      toast.error('Could not copy the traceability register');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-500">
          {requirements.length} on this page · {total} requirements
        </div>
        <div className="flex flex-wrap gap-2">
          {hasFilters && (
            <Button variant="outline" size="sm" onClick={onClearFilters}>
              <FilterX className="mr-1 h-4 w-4" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={copyPage}>
            <Copy className="mr-1 h-4 w-4" /> Copy
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isExportingAll}>
                <Download className="mr-1 h-4 w-4" />
                {isExportingAll ? 'Exporting…' : 'CSV'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export traceability</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportRows(requirements, 'page')}>
                Export page ({requirements.length})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportAll}>Export all ({total})</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('mr-1 h-4 w-4', isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="mr-1 h-4 w-4" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columnState.orderedColumns
                .filter((column) => column.key !== 'actions')
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={columnState.isVisible(column.key)}
                    onCheckedChange={() => columnState.toggleColumn(column.key)}
                  >
                    {column.label}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DataTable
        items={requirements}
        columns={columnState.visibleColumns}
        ctx={undefined}
        columnFilters={columnFilters}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={(key) => onSort(key as ValidationRequirementFilters['sortKey'])}
        onSetColumnFilter={onColumnFilterChange}
        isLoading={isLoading}
        emptyState="No requirements match these readiness filters."
        className="border-slate-200 shadow-sm"
      />

      <Pagination
        page={page + 1}
        pageSize={pageLength}
        total={total}
        onPageChange={(nextPage) => onPageChange(nextPage - 1)}
      />
    </div>
  );
}
